import { useEffect, useState } from "react";
import { Link } from "@frontend/lib/link";
import { AccountSwitcher } from "@frontend/components/account-switcher";
import { NewCollabButton } from "@frontend/components/new-collab-button";
import { ProfileMenu } from "@frontend/components/profile-menu";
import { WorkspaceNav } from "@frontend/components/workspace-nav";
import { BrandLogo } from "@frontend/components/brand-logo";
import { LinkButton, cx } from "@frontend/components/ui";
import { clientApi } from "@frontend/lib/api";
import { creatorAvatarUrl } from "@frontend/lib/avatar";
import { collabsCacheKey } from "@frontend/lib/data-cache";
import { collabNeedsYou } from "@frontend/lib/collab-tabs";
import { useAsync } from "@frontend/lib/use-async";
import type { Viewer } from "@creators/backend/lib/auth";
import type { CollabListRow } from "@creators/backend/lib/collabs/queries";

/**
 * Who is signed in, which is not the same as the account in view. The profile menu
 * names the person; the switcher beside it names the account they are working in.
 */
function displayName(viewer: Viewer): string {
  if (viewer.isAdmin) return "Admin";
  const owned = viewer.accounts.find((account) => account.own);
  return owned?.displayName ?? viewer.brand?.name ?? viewer.phone ?? "Account";
}

/**
 * The photo belongs to the person's *own* creator account, not to whichever
 * account they happen to be standing in — the menu names them, not the account.
 */
function ownAvatar(viewer: Viewer): string | null {
  if (viewer.isAdmin) return null;
  const owned = viewer.accounts.find((account) => account.own);
  return owned ? creatorAvatarUrl(owned.creatorId, owned.avatarKey) : null;
}

function ownPagePath(viewer: Viewer): string | null {
  if (viewer.isAdmin) return null;
  const owned = viewer.accounts.find((account) => account.own);
  return owned?.profilePath ?? null;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

/**
 * True while a section marked `data-nav-dark` sits under the floating header.
 *
 * The marketing bar is mostly transparent, so it takes the colour of whatever
 * scrolls beneath it: over a dark band its ink labels would disappear. The
 * observer's root is squeezed to a thin strip at the top of the viewport, which
 * is the only place a section can actually be behind the bar.
 */
function useOverDarkSection(enabled: boolean): boolean {
  const [overDark, setOverDark] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const sections = document.querySelectorAll("[data-nav-dark]");
    if (sections.length === 0) return;

    const covering = new Set<Element>();
    let observer: IntersectionObserver | null = null;

    const observe = () => {
      observer?.disconnect();
      covering.clear();
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) covering.add(entry.target);
            else covering.delete(entry.target);
          }
          setOverDark(covering.size > 0);
        },
        { rootMargin: `0px 0px -${Math.max(0, window.innerHeight - 76)}px 0px` },
      );
      for (const section of sections) observer.observe(section);
    };

    observe();
    window.addEventListener("resize", observe);
    return () => {
      window.removeEventListener("resize", observe);
      observer?.disconnect();
    };
  }, [enabled]);

  return overDark;
}

export function TopNav({
  viewer,
  links,
}: {
  viewer: Viewer | null;
  /** Marketing sections, passed only by the public site. */
  links?: { label: string; href: string }[];
}) {
  const name = viewer ? displayName(viewer) : "";
  const inWorkspace = Boolean(viewer && !viewer.isAdmin);
  const cacheKey = collabsCacheKey(
    viewer?.sessionId,
    !viewer || viewer.isAdmin ? null : viewer.creator?.id,
  );

  const inbox = useAsync(
    async () => {
      if (!viewer || viewer.isAdmin) return [] as CollabListRow[];
      const { collabs } = await clientApi<{ collabs: CollabListRow[] }>("/api/collabs");
      return collabs;
    },
    [viewer?.sessionId, viewer?.isAdmin, viewer?.creator?.id],
    { cacheKey },
  );

  const actionCount = (inbox.data ?? []).filter(collabNeedsYou).length;
  /** Marketing pages: float over the hero so glass can blur the dark stage. */
  const overlay = Boolean(links);
  const overDark = useOverDarkSection(overlay);

  return (
    <header
      className={
        overlay
          ? "pointer-events-none fixed inset-x-0 top-0 z-20 px-3 pt-[max(0.5rem,env(safe-area-inset-top))] sm:px-4"
          : "pointer-events-none sticky top-0 z-20 px-3 pt-[max(0.5rem,env(safe-area-inset-top))] sm:px-4"
      }
    >
      <div
        className={
          overlay
            ? cx(
                "mkt-nav glass pointer-events-auto relative mx-auto w-full max-w-6xl rounded-full",
                overDark && "mkt-nav-invert",
              )
            : "glass pointer-events-auto relative mx-auto w-full max-w-6xl rounded-[1.35rem]"
        }
      >
        <div className="relative mx-auto grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5">
          <div className="flex min-w-0 items-center gap-2 justify-self-start sm:gap-3">
            <Link
              href={viewer && !viewer.isAdmin ? "/overview" : "/"}
              className="flex shrink-0 items-center"
              aria-label="SendCollab"
            >
              <BrandLogo inverse={overDark} />
            </Link>

            {inWorkspace && viewer ? (
              <AccountSwitcher
                accounts={viewer.accounts}
                activeCreatorId={viewer.creator?.id ?? null}
                personalLabel={viewer.brand?.name ?? viewer.phone ?? "Personal"}
              />
            ) : null}
          </div>

          <div className="justify-self-center">
            {inWorkspace && viewer ? (
              <WorkspaceNav showAccess={Boolean(viewer.creator)} actionCount={actionCount} />
            ) : links ? (
              <nav aria-label="Page sections" className="hidden items-center gap-0.5 md:flex">
                {links.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={cx(
                      "rounded-full px-3 py-1.5 text-sm transition",
                      overDark
                        ? "text-white/70 hover:bg-white/10 hover:text-white"
                        : "text-ink/65 hover:bg-ink/5 hover:text-ink",
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            ) : null}
          </div>

          {viewer ? (
            <div className="flex shrink-0 items-center justify-end gap-1.5 justify-self-end sm:gap-3">
              {!viewer.isAdmin && viewer.brand ? <NewCollabButton /> : null}
              <ProfileMenu
                name={name}
                phone={viewer.phone}
                initials={initials(name)}
                avatarUrl={ownAvatar(viewer)}
                pagePath={ownPagePath(viewer)}
                sharedAccess={Boolean(
                  !viewer.isAdmin && viewer.membership && !viewer.membership.own,
                )}
              />
            </div>
          ) : (
            <nav className="flex shrink-0 items-center justify-end justify-self-end text-sm">
              {overlay ? (
                <Link
                  href="/login"
                  className={cx(
                    "rounded-full px-3.5 py-1.5 text-sm font-semibold transition",
                    overDark
                      ? "bg-white text-ink hover:bg-white/90"
                      : "bg-ink text-white hover:bg-ink/90",
                  )}
                >
                  Get started
                </Link>
              ) : (
                <LinkButton href="/login" size="sm">
                  Get started
                </LinkButton>
              )}
            </nav>
          )}
        </div>
      </div>
    </header>
  );
}
